import { MouseEvent, useCallback, useMemo, useState } from 'react';

import clsx from 'clsx';
import {
  TableBody as MUITableBody,
  TableRowProps as MuiRowProps,
  TableRow,
  TableCell,
 Skeleton } from '@mui/material';

import { TableColumn } from '../Table.types';
import { AlignTypes } from 'models/commonTypes';
import { EmptyResultsCell, EditableRow } from './components';
import { useTableBodyStyles } from './TableBody.styles';

type TableBodyProps = {
  loading: boolean;
  rowsPerPage: number;
  columns: Array<TableColumn>;
  rows: Array<any>;
  hasFilters: boolean;
  cellPadding: number | string;
  onEditCell?: (columnId, value, row) => Promise<any>;
  idLocator?: (param: any) => any;
  hover?: MuiRowProps['hover'];
  hoverForExpandableRows?: boolean;
  hasExpandableRows?: boolean;
  enableMultiRowSelection?: boolean;
  selectedRowIds?: Array<string | number>;
  onSelectedRowIdsChange?: (selectedRowIds: Array<string | number>) => void;
  onRowDoubleClick?: (row: any) => void;
};

export const TableBody = ({
  loading,
  rowsPerPage,
  columns,
  rows,
  idLocator,
  hasFilters,
  cellPadding,
  hover,
  hoverForExpandableRows,
  onEditCell,
  hasExpandableRows = false,
  enableMultiRowSelection = false,
  selectedRowIds = [],
  onSelectedRowIdsChange,
  onRowDoubleClick,
}: TableBodyProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectionAnchorIndex, setSelectionAnchorIndex] = useState<number | null>(null);
  const classes = useTableBodyStyles({ cellPadding });

  const getRowId = useCallback(row => (idLocator ? idLocator(row) : row.id), [idLocator]);

  const selectedRowIdsSet = useMemo(
    () =>
      new Set(
        selectedRowIds
          .filter(selectedRowId => selectedRowId !== undefined && selectedRowId !== null)
          .map(selectedRowId => selectedRowId.toString()),
      ),
    [selectedRowIds],
  );

  const emitSelectedRowIds = useCallback(
    (nextSelectedRowIdsSet: Set<string>) => {
      if (!onSelectedRowIdsChange) {
        return;
      }

      const nextSelectedRowIds = rows
        .map(getRowId)
        .filter(rowId => rowId !== undefined && rowId !== null)
        .filter(rowId => nextSelectedRowIdsSet.has(rowId.toString()));

      onSelectedRowIdsChange(nextSelectedRowIds);
    },
    [getRowId, onSelectedRowIdsChange, rows],
  );

  const handleExpandRow = useCallback(
    rowIndex => {
      const rowId = (idLocator ? idLocator(rows[rowIndex]) : rows[rowIndex].id).toString();
      const newExpandedRows = new Set(expandedRows);
      if (newExpandedRows.has(rowId)) {
        newExpandedRows.delete(rowId);
      } else {
        newExpandedRows.add(rowId);
      }
      setExpandedRows(newExpandedRows);
    },
    [expandedRows, idLocator, rows],
  );

  const skeletonWidthsPct = ['72%', '88%', '64%', '92%', '56%', '80%', '70%'];
  const skeletonRowCount = Math.max(1, rowsPerPage);

  const handleRowSelection = useCallback(
    (event: MouseEvent<HTMLTableRowElement>, rowIndex: number) => {
      if (!enableMultiRowSelection) {
        return;
      }

      const target = event.target as HTMLElement;
      const shouldIgnoreSelection = !!target.closest(
        'button, a, input, textarea, select, [role="button"], [contenteditable="true"]',
      );

      if (shouldIgnoreSelection) {
        return;
      }

      const clickedRowId = getRowId(rows[rowIndex]);
      const clickedRowKey = clickedRowId?.toString();

      if (!clickedRowKey) {
        return;
      }

      const isCtrlOrCmdPressed = event.ctrlKey || event.metaKey;
      const nextSelectedRowIdsSet = new Set(selectedRowIdsSet);

      const resolvedAnchorIndex = selectionAnchorIndex ?? rowIndex;

      if (event.shiftKey) {
        const startIndex = Math.min(resolvedAnchorIndex, rowIndex);
        const endIndex = Math.max(resolvedAnchorIndex, rowIndex);
        const rangedRowKeys = rows
          .slice(startIndex, endIndex + 1)
          .map(getRowId)
          .filter(rowId => rowId !== undefined && rowId !== null)
          .map(rowId => rowId.toString());

        if (!isCtrlOrCmdPressed) {
          nextSelectedRowIdsSet.clear();
        }

        rangedRowKeys.forEach(rowKey => nextSelectedRowIdsSet.add(rowKey));
      } else if (isCtrlOrCmdPressed) {
        if (nextSelectedRowIdsSet.has(clickedRowKey)) {
          nextSelectedRowIdsSet.delete(clickedRowKey);
        } else {
          nextSelectedRowIdsSet.add(clickedRowKey);
        }
      } else {
        nextSelectedRowIdsSet.clear();
        nextSelectedRowIdsSet.add(clickedRowKey);
      }

      if (selectionAnchorIndex === null || !event.shiftKey) {
        setSelectionAnchorIndex(rowIndex);
      }
      emitSelectedRowIds(nextSelectedRowIdsSet);
    },
    [
      emitSelectedRowIds,
      enableMultiRowSelection,
      getRowId,
      rows,
      selectedRowIdsSet,
      selectionAnchorIndex,
    ],
  );

  const handleRowMouseDown = useCallback(
    (event: MouseEvent<HTMLTableRowElement>) => {
      if (!enableMultiRowSelection || !event.shiftKey) {
        return;
      }

      event.preventDefault();
    },
    [enableMultiRowSelection],
  );

  const handleRowDoubleClick = useCallback(
    (event: MouseEvent<HTMLTableRowElement>, rowIndex: number) => {
      if (!onRowDoubleClick) {
        return;
      }

      const target = event.target as HTMLElement;
      const shouldIgnoreDoubleClick = !!target.closest(
        'button, a, input, textarea, select, [role="button"], [contenteditable="true"]',
      );

      if (shouldIgnoreDoubleClick) {
        return;
      }

      onRowDoubleClick(rows[rowIndex]);
    },
    [onRowDoubleClick, rows],
  );

  return (
    <MUITableBody>
      {loading ? (
        Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
          <TableRow key={`table-skeleton-${rowIndex}`}>
            {hasExpandableRows ? (
              <TableCell
                className={clsx(classes.padding, classes.skeletonExpandCell)}
                padding="checkbox"
              >
                <Skeleton animation="wave" variant="rectangular" width={28} height={28} />
              </TableCell>
            ) : null}
            {(columns.length ? columns : [{ id: 'skeleton' } as TableColumn]).map(
              (column, colIndex) => {
                const align = column.align ?? AlignTypes.LEFT;
                const widthPct = skeletonWidthsPct[colIndex % skeletonWidthsPct.length] ?? '75%';
                return (
                  <TableCell
                    key={`${column.id}-skeleton-${rowIndex}`}
                    align={align}
                    className={clsx(classes.padding, classes.skeletonCell)}
                  >
                    <Skeleton animation="wave" variant="text" height={22} width={widthPct} />
                  </TableCell>
                );
              },
            )}
          </TableRow>
        ))
      ) : rows.length ? (
        rows.map((row, rowIndex) => {
          const rowId = (getRowId(rows[rowIndex]) ?? rowIndex).toString();
          return (
            <EditableRow
              hover={hover}
              hoverForExpandableRows={hoverForExpandableRows}
              key={rowIndex}
              columns={columns}
              data={row}
              rowId={rowIndex}
              isExpanded={expandedRows.has(rowId)}
              classes={classes}
              onExpandRow={handleExpandRow}
              onEditCell={onEditCell}
              hasExpandableRows={hasExpandableRows}
              isSelected={selectedRowIdsSet.has(rowId)}
              onRowClick={event => handleRowSelection(event, rowIndex)}
              onRowDoubleClick={
                onRowDoubleClick
                  ? event => handleRowDoubleClick(event, rowIndex)
                  : undefined
              }
              onRowMouseDown={handleRowMouseDown}
            />
          );
        })
      ) : (
        <TableRow>
          <EmptyResultsCell colSpan={columns.length} hasFilters={hasFilters} />
        </TableRow>
      )}
    </MUITableBody>
  );
};
